import { call, put, takeLatest } from 'redux-saga/effects';
import {
  SUREST_FILTER_DATA_REQUEST,
  SUREST_RELATIVE_GRAPH_DATA_REQUEST,
  SUREST_RELATIVE_COST_DRIVERS_REQUEST,
  SUREST_RELATIVE_COST_DRIVERS_SUCCESS,
  SUREST_RELATIVE_DISTRIBUTION_SUCCESS,
  SUREST_RELATIVE_DISTRIBUTION_REQUEST,
  SUREST_RELATIVE_COMMON_SERVICES_REQUEST,
} from '@src/middleware/actions/actionTypes';
import {
  getSurestFilterDataError,
  getSurestFilterDataSuccess,
  setSurestFilterLoading,
  getSurestRelativeGraphSuccess,
  getSurestRelativeGraphError,
  setSurestRelativeGraphLoading,
} from '@src/middleware/actions/Surest/surestFilterActions';
import {
  setRelativeCostDriverData,
  setRelativeCostDriverError,
  setRelativeDistributionError,
  setRelativeCommonServicesData,
  setRelativeCommonServicesError,
} from '@src/middleware/actions/Surest/costDriversActions';
import {
  getSurestFilterData,
  getSurestRelativeGraphData,
  getSurestRelativeCostDrivers,
  getSurestRelativeDistributionData,
  getSurestRelativeCommonServices,
} from '@src/middleware/services/surest/surest.service';

interface SurestRequestPayload {
  [key: string]: unknown;
}

interface SurestApiResponse {
  Data?: unknown;
  Errors?: string[];
  StatusCode?: number;
  Message?: string;
  LocationOptions?: unknown;
  OfficeOptions?: unknown;
}

interface SurestSagaAction {
  payload?: SurestRequestPayload;
}

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'object' && error !== null && 'Message' in error) {
    return String(error.Message);
  }

  return 'Unknown error occurred';
};

export function* fetchSurestFilterData(action: SurestSagaAction) {
  try {
    yield put(setSurestFilterLoading(true));
    const payload = action?.payload || {};
    const response = (yield call(getSurestFilterData, payload)) as SurestApiResponse;
    const responseData = response?.Data || [];

    if (payload.pricingBasis === 'location' && response?.LocationOptions) {
      yield put(getSurestFilterDataSuccess({ data: response.LocationOptions }));
    } else if (payload.pricingBasis === 'office' && response?.OfficeOptions) {
      yield put(getSurestFilterDataSuccess({ data: response.OfficeOptions }));
    } else {
      yield put(getSurestFilterDataSuccess({ data: responseData }));
    }
  } catch (error: unknown) {
    yield put(
      getSurestFilterDataError({
        message: getErrorMessage(error),
        stack: error instanceof Error ? error.stack || '' : '',
        source: 'fetchSurestFilterData',
      })
    );
  } finally {
    yield put(setSurestFilterLoading(false));
  }
}

export function* fetchSurestRelativeGraphData(action: SurestSagaAction) {
  try {
    yield put(setSurestRelativeGraphLoading(true));
    const payload = action?.payload || {};
    const response = (yield call(getSurestRelativeGraphData, payload)) as SurestApiResponse;
    if (response?.Data) {
      yield put(getSurestRelativeGraphSuccess(response.Data));
    } else {
      yield put(
        getSurestRelativeGraphError({
          message: response?.Errors?.[0] || 'No data found for relative graph.',
          statusCode: response?.StatusCode || 500,
        })
      );
    }
  } catch (error: unknown) {
    yield put(
      getSurestRelativeGraphError({
        message: getErrorMessage(error),
        statusCode: 500,
      })
    );
  } finally {
    yield put(setSurestRelativeGraphLoading(false));
  }
}

export function* fetchSurestRelativeCostDrivers(action: SurestSagaAction) {
  try {
    const response = (yield call(
      getSurestRelativeCostDrivers,
      action?.payload || {}
    )) as SurestApiResponse;
    if (response?.Data) {
      yield put({
        type: SUREST_RELATIVE_COST_DRIVERS_SUCCESS,
        payload: response?.Data || [],
      });
      yield put(setRelativeCostDriverData(response?.Data || {}));
    } else {
      yield put(
        setRelativeCostDriverError({
          message: response?.Errors[0] || 'No data found for relative cost drivers.',
          statusCode: response?.StatusCode || 500,
        })
      );
    }
  } catch (error: unknown) {
    yield put(
      setRelativeCostDriverError({
        message: getErrorMessage(error),
        statusCode: 500,
      })
    );
  }
}

export function* fetchSurestRelativeDistributionData(action: SurestSagaAction) {
  try {
    const response = (yield call(getSurestRelativeDistributionData, action?.payload || {})) as
      | SurestApiResponse
      | unknown[];
    // Service returns an array directly; treat response as array
    if (Array.isArray(response) && response.length) {
      yield put({
        type: SUREST_RELATIVE_DISTRIBUTION_SUCCESS,
        payload: response,
      });
    } else if (response.Data) {
      // fallback if service changes to object shape
      yield put({
        type: SUREST_RELATIVE_DISTRIBUTION_SUCCESS,
        payload: response.Data || [],
      });
    } else {
      yield put(
        setRelativeDistributionError({
          message: response.Errors?.[0] || 'No data found for relative distribution.',
          statusCode: response.StatusCode || 500,
        })
      );
    }
  } catch (error: unknown) {
    yield put(
      setRelativeDistributionError({
        message: getErrorMessage(error),
        statusCode: 500,
      })
    );
  }
}

export function* fetchSurestRelativeCommonServices(action: SurestSagaAction) {
  try {
    const response = (yield call(
      getSurestRelativeCommonServices,
      action?.payload || {}
    )) as SurestApiResponse;
    if (response?.Data) {
      yield put(setRelativeCommonServicesData(response.Data));
    } else {
      yield put(
        setRelativeCommonServicesError({
          message: response?.Message || 'No data found for relative common services.',
          statusCode: response?.StatusCode || 500,
        })
      );
    }
  } catch (error: unknown) {
    yield put(
      setRelativeCommonServicesError({
        message: getErrorMessage(error),
        statusCode: 500,
      })
    );
  }
}
export function* surestSaga() {
  yield takeLatest(SUREST_FILTER_DATA_REQUEST, fetchSurestFilterData);
  yield takeLatest(SUREST_RELATIVE_GRAPH_DATA_REQUEST, fetchSurestRelativeGraphData);
  yield takeLatest(SUREST_RELATIVE_COST_DRIVERS_REQUEST, fetchSurestRelativeCostDrivers);
  yield takeLatest(SUREST_RELATIVE_DISTRIBUTION_REQUEST, fetchSurestRelativeDistributionData);
  yield takeLatest(SUREST_RELATIVE_COMMON_SERVICES_REQUEST, fetchSurestRelativeCommonServices);
}

export default surestSaga;
